import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import LoadingButton from "@enerex/unified-ui-react/LoadingButton";
import FormSelect from "@enerex/unified-ui-react/form/Select";


import Table from "@enerex/unified-ui-react/Table";
import ActionsMenu from "@enerex/unified-ui-react/ActionsMenu";
import EditStateOperationModal, { getSopEditSnapshot } from "./EditStateOperationModal";
import useSopEditSave from "./useSopEditSave";
import { getSelectedRows } from "./utilityTableSelection";
import { Link, useLocation, useParams } from "react-router-dom";
import useShowSnackbar from "hooks/useShowSnackbar";
import { useAuth } from "providers/CustomAuthProvider";
import * as api from "../utilityApi";
import { CommodityBadges } from "../CommodityIcons";
import { EMPTY_GUID, LogoPreview, buildUtilitySavePayload, getActionId, getEnerexIdentifier, getStateAbbreviation, getFormatStateName, getIdentifier, getStateName, getStateOptionValue, getUserName, isUtilityActive, textOrDash } from "../utilityUtils";
import UtilityFormModal from "../list/UtilityFormModal";
import StateOperationModal from "./StateOperationModal";
import AliasModal from "./AliasModal";
import RateClassModal from "./RateClassModal";
import FormatModal from "./FormatModal";
import HistoryModal from "./HistoryModal";

const utilityDetailRequests = new Map();

const sharedUtilityRequest = (key, request) => {
  const existing = utilityDetailRequests.get(key);
  if (existing) return existing;
  const promise = Promise.resolve().then(request).finally(() => utilityDetailRequests.delete(key));
  utilityDetailRequests.set(key, promise);
  return promise;
};

const useDetailPagination = () => {
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);

  return {
    limit,
    setLimit,
    page,
    setPage,
    search: "",
    setSearch: () => {},
    orderBy: undefined,
    setOrderBy: () => {},
    ascendingOrder: true,
    setAscendingOrder: () => {},
    filters: null,
    setFilters: () => {},
    extra: null,
  };
};

const UtilityDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const utilityListLocation = location.state?.utilityListLocation || "/settings/lookup/utilities";
  const snackbar = useShowSnackbar();
  const snackbarRef = useRef(snackbar);
  useEffect(() => { snackbarRef.current = snackbar; }, [snackbar]);
  const { user } = useAuth();
  const currentUserName = getUserName(user);
  const [utility, setUtility] = useState(null);
  const [utilityOptions, setUtilityOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [lookupStates, setLookupStates] = useState([]);
  const [aliasStates, setAliasStates] = useState([]);
  const [utilityStates, setUtilityStates] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [utilityRateClasses, setUtilityRateClasses] = useState([]);
  const [aliasRowSelection, setAliasRowSelection] = useState({});
  const [rateClassRowSelection, setRateClassRowSelection] = useState({});
  const [bulkAction, setBulkAction] = useState({ type: "", recordType: "" });
  const [transferForm, setTransferForm] = useState({ utility: "", state: "", commodity: "" });
  const [bulkSaving, setBulkSaving] = useState(false);

  const [utilityFormats, setUtilityFormats] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [showRateClassModal, setShowRateClassModal] = useState(false);
  const [editingRateClass, setEditingRateClass] = useState(null);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [editingFormat, setEditingFormat] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, row: null, message: "", onConfirm: null });
  const detailLoadedRef = useRef("");
  const detailLookupsLoadedRef = useRef("");
  const rateClassesLoadedRef = useRef("");
  const formatsLoadedRef = useRef("");
  const historyLoadedRef = useRef("");


  const selectedAliasRows = getSelectedRows(aliases, aliasRowSelection);
  const selectedRateClassRows = getSelectedRows(utilityRateClasses, rateClassRowSelection);
  const selectedAliasIds = selectedAliasRows.map(getActionId).filter((recordId) => recordId != null && recordId !== "").map(String);
  const selectedRateClassIds = selectedRateClassRows.map(getActionId).filter((recordId) => recordId != null && recordId !== "").map(String);
  const selectedBulkRows = bulkAction.recordType === "aliases"
    ? selectedAliasRows
    : selectedRateClassRows;
  const bulkRecordLabel = bulkAction.recordType === "aliases" ? "Aliases" : "Rate Class";
  const bulkRecordLabelPlural = bulkAction.recordType === "aliases" ? "aliases" : "rate classes";
  const closeBulkModal = () => setBulkAction({ type: "", recordType: "" });
  const responseMessage = (result, fallback) => result?.message || result?.Message || fallback;
  const responseWarning = (result) =>
    result?.data?.warningMessage ||
    result?.data?.WarningMessage ||
    result?.Data?.warningMessage ||
    result?.Data?.WarningMessage ||
    "";
  const confirmBulkDelete = async () => {
    if (bulkSaving) return;
    setBulkSaving(true);
    try {
      const isAlias = bulkAction.recordType === "aliases";
      const ids = isAlias ? [...selectedAliasIds] : [...selectedRateClassIds];
      const result = isAlias
        ? await api.removeUtilityAliases({ aliasIds: ids })
        : await api.removeUtilityRateClasses({ rateClassIds: ids });
      if (isAlias) {
        setAliasRowSelection({});
        await loadAliases();
      } else {
        setRateClassRowSelection({});
        rateClassesLoadedRef.current = "";
        await loadRateClasses();
      }
      closeBulkModal();
      snackbar(responseMessage(result, "Selected records deleted successfully."), "success");
    } catch (error) {
      snackbar(error.message || "Bulk delete failed.", "error");
    } finally {
      setBulkSaving(false);
    }
  };
  const confirmBulkTransfer = async () => {
    if (bulkSaving) return;
    setBulkSaving(true);
    try {
      const isAlias = bulkAction.recordType === "aliases";
      const ids = isAlias ? [...selectedAliasIds] : [...selectedRateClassIds];
      const payload = {
        ...(isAlias ? { sourceAliasIds: ids } : { sourceRateClassIds: ids }),
        ...(transferForm.utility && { destinationUtilityIdentifier: transferForm.utility }),
        ...(isAlias
          ? {}
          : transferForm.state && { destinationStateId: Number(transferForm.state) }),
        ...(!isAlias && transferForm.commodity && { destinationCommodityId: Number(transferForm.commodity) }),
      };
      const result = isAlias
        ? await api.transferUtilityAliases(payload)
        : await api.transferUtilityRateClasses(payload);
      if (isAlias) {
        setAliasRowSelection({});
        await loadAliases();
      } else {
        setRateClassRowSelection({});
        rateClassesLoadedRef.current = "";
        await loadRateClasses();
      }
      closeBulkModal();
      const warningMessage = isAlias ? "" : responseWarning(result);
      snackbar(
        warningMessage || responseMessage(result, "Selected records transferred successfully."),
        warningMessage ? "warning" : "success"
      );
    } catch (error) {
      snackbar(error.message || "Transfer failed.", "error");
    } finally {
      setBulkSaving(false);
    }
  };


  const detailCardStyle = {
    backgroundColor: "#fff",
    borderRadius: 4,
    boxShadow: "0 0 35px 0 rgba(154, 161, 171, 0.15)",
    marginBottom: 24,
  };

  const detailCardBodyStyle = {
    padding: 24,
  };

  const detailSectionTitleStyle = {
    color: "inherit",
    fontSize: "15px",
    lineHeight: 1.2,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const loadDetail = useCallback(async () => {
    if (detailLoadedRef.current === id)
      return;

    detailLoadedRef.current = id;
    try {
      const data = await sharedUtilityRequest(`detail:${id}`, () => api.getUtilityByIdentifier(id));
      setUtility(data);
      const nextUtilityStates = data?.utilityStates || data?.UtilityStates || [];
      const nextAliases = data?.aliases || data?.Aliases || [];
      setUtilityStates(nextUtilityStates);
      setAliasStates(nextUtilityStates);
      setAliases(nextAliases);
    } catch (error) {
      console.error("Failed to load utility", error);
    }
  }, [id]);

  const loadStatesForCountry = useCallback(async (countryIdentifier) => {
    const stateLookup = countryIdentifier
      ? await sharedUtilityRequest(`states:${countryIdentifier}`, () => api.getStatesByCountry(countryIdentifier))
      : await sharedUtilityRequest("states:all", () => api.getStates());
    const nextStates = Array.isArray(stateLookup) ? stateLookup : [];
    setLookupStates(nextStates);
    return nextStates;
  }, []);

  const loadLookups = useCallback(async () => {
    if (detailLookupsLoadedRef.current === id)
      return;

    detailLookupsLoadedRef.current = id;
    try {
      const [utilityLookup, countryLookup] = await Promise.all([
        sharedUtilityRequest(`utility-options:${id}`, () => api.getUtilityOptions()),
        sharedUtilityRequest(`countries:${id}`, () => api.getCountries()),
      ]);
      const nextCountries = Array.isArray(countryLookup) ? countryLookup : [];
      const defaultCountry =
        nextCountries.find((country) => (country.name || country.Name) === "USA") ||
        nextCountries[0];

      setUtilityOptions(utilityLookup);
      setCountries(nextCountries);
      await loadStatesForCountry(getIdentifier(defaultCountry) || "");
    } catch (error) {
      console.error("Failed to load utility lookups", error);
      setUtilityOptions([]);
      setCountries([]);
      setLookupStates([]);
    }
  }, [id, loadStatesForCountry]);

  const loadRateClasses = useCallback(async () => {
    if (rateClassesLoadedRef.current === id)
      return;

    try {
      const data = await sharedUtilityRequest(`rate-classes:${id}`, () => api.getUtilityRateClasses(id));
      setUtilityRateClasses(Array.isArray(data) ? data : []);
      setRateClassRowSelection({});
      rateClassesLoadedRef.current = id;
    } catch (error) {
      console.error("Failed to load utility rate classes", error);
      rateClassesLoadedRef.current = "";
      setUtilityRateClasses([]);
    }
  }, [id]);

  const loadFormats = useCallback(async () => {
    if (formatsLoadedRef.current === id)
      return;

    formatsLoadedRef.current = id;
    try {
      const data = await sharedUtilityRequest(`formats:${id}`, () => api.getUtilityFormats(id));
      setUtilityFormats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load utility formats", error);
      setUtilityFormats([]);
    }
  }, [id]);

  const loadStateOperations = useCallback(async () => {
    try {
      const data = await sharedUtilityRequest(`state-operations:${id}`, () => api.getUtilityStateOperations(id));
      const nextStates = Array.isArray(data) ? data : [];
      setUtilityStates(nextStates);
      setAliasStates(nextStates);
    } catch (error) {
      console.error("Failed to load utility state operations", error);
    }
  }, [id]);

  const loadAliases = useCallback(async () => {
    try {
      const data = await sharedUtilityRequest(`aliases:${id}`, () => api.getUtilityAliases(id));
      setAliases(Array.isArray(data) ? data : []);
      setAliasRowSelection({});
    } catch (error) {
      console.error("Failed to load utility aliases", error);
    }
  }, [id]);

  const loadHistory = useCallback(async () => {
    if (historyLoadedRef.current === id)
      return;

    historyLoadedRef.current = id;
    try {
      const data = await api.getUtilityActivityLog(id);
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load utility history", error);
      setHistory([]);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
    loadLookups();
    loadAliases();
    loadRateClasses();
    loadFormats();
  }, [loadDetail, loadLookups, loadAliases, loadRateClasses,loadFormats]);

  const resetDetailLoadCache = () => {
    detailLoadedRef.current = "";
    rateClassesLoadedRef.current = "";
    formatsLoadedRef.current = "";
    historyLoadedRef.current = "";
  };

  const refreshAll = async () => {
    resetDetailLoadCache();
    await Promise.all([
      loadDetail(),
      loadAliases(),
      loadStateOperations(),
      loadRateClasses(),
      loadFormats(),
      loadHistory(),
    ]);
  };

  const saveMaster = async (payload) => {
    setSaving(true);
    try {
      const result = await api.saveUtility(buildUtilitySavePayload(payload, currentUserName));
      if (result === null || result === "AlreadyExist") {
        snackbar("Utility already exists", "warning");
        return;
      }
      snackbar("Utility updated successfully", "success");
      setShowEdit(false);
      resetDetailLoadCache();
      await refreshAll();
    } catch (error) {
      snackbar(error.message || "Failed to save utility", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveStateOperation = async (payload) => {
    setSaving(true);
    try {
      const commodityIds = (payload.CommodityIds?.length ? payload.CommodityIds : [payload.CommodityId])
        .map((commodityId) => Number(commodityId))
        .filter((commodityId) => commodityId === 1 || commodityId === 2);
      const stateId = String(payload.StateId || "");
      const duplicateCommodityIds = utilityStates
        .filter((row) => String(row.stateId || row.StateId || "") === stateId)
        .flatMap((row) => {
          const mappings = row.commodityMappings ?? row.CommodityMappings;
          if (Array.isArray(mappings) && mappings.length) {
            return mappings.map((mapping) => Number(mapping.commodityId ?? mapping.CommodityId));
          }
          const rowCommodityId = Number(row.commodityId || row.CommodityId || 0);
          return rowCommodityId === 3 || rowCommodityId === 5 ? [1, 2] : [rowCommodityId];
        });
      const exactDuplicateExists = commodityIds.every((commodityId) => duplicateCommodityIds.includes(commodityId));

      if (!commodityIds.length) {
        snackbar("Select at least one commodity", "warning");
        return;
      }

      if (exactDuplicateExists) {
        snackbar("State already exists", "warning");
        return;
      }

      const newCommodityIds = commodityIds.filter((commodityId) => !duplicateCommodityIds.includes(commodityId));
      const primaryCommodityId = newCommodityIds.length === 2 ? 3 : newCommodityIds[0];
      const result = await api.saveUtilityStateOperation({
        StateIds: payload.StateId ? [Number(payload.StateId)] : [],
        CommodityIds: newCommodityIds,
        CommodityMappings: newCommodityIds.map((commodityId) => ({ Id: "0", CommodityId: commodityId })),
        UtilityId: 0,
        CommodityId: primaryCommodityId,
        Electric: newCommodityIds.includes(1),
        Gas: newCommodityIds.includes(2),
        NatGas: newCommodityIds.includes(2),
        StateId: stateId,
        StateName:
          payload.SelectedState?.stateName ||
          payload.SelectedState?.StateName ||
          payload.StateName ||
          "",
        UtilityName: utility?.name || utility?.Name || "",
        EnerexUtilityId: id || EMPTY_GUID,
        EnerexStateId: payload.SelectedState?.enerexIdentifier || payload.SelectedState?.EnerexIdentifier || EMPTY_GUID,
        EnerexUtilityStateId: EMPTY_GUID,
        UserName: currentUserName,
        ServiceReferenceNumberRequired: Boolean(payload.ServiceReferenceNumberRequired),
        ServiceReferenceNumberDescription: payload.ServiceReferenceNumberDescription || null,
        KeyNameRequired: Boolean(payload.KeyNameRequired),
        KeyNameDescription: payload.KeyNameDescription ?? "",
        EnrollmentLeadTime: payload.EnrollmentLeadTime ?? "",
      });
      if (!result || (Array.isArray(result) && result.length === 0)) {
        snackbar("State of operation already existed", "warning");
        return;
      }
      const nextStates = await api.getUtilityStateOperations(id);
      const nextStateRows = Array.isArray(nextStates) ? nextStates : [];
      setUtilityStates(nextStateRows);
      setAliasStates(nextStateRows);

      snackbar("Utility state operation saved successfully", "success");
      setShowStateModal(false);
      detailLoadedRef.current = "";
      historyLoadedRef.current = "";
      await Promise.all([loadDetail(), loadHistory()]);
    } catch (error) {
      snackbar(error.message || "Failed to save state operation", "error");
    } finally {
      setSaving(false);
    }
  };

  const editStateOperation = async (row) => {
    try {
      const states = await api.getUtilityStateOperations(id);
      const stateGuid = row.enerexStateId ?? row.EnerexStateId;
      const fresh = states.find((state) =>
        stateGuid && stateGuid !== EMPTY_GUID
          ? String(state.enerexStateId ?? state.EnerexStateId).toLowerCase() === String(stateGuid).toLowerCase()
          : String(state.stateId ?? state.StateId) === String(row.stateId ?? row.StateId));
      if (!fresh || !getSopEditSnapshot(fresh)) throw new Error("State mapping details are unavailable. Refresh the page before editing.");
      if (!(fresh.enerexStateId ?? fresh.EnerexStateId) || (fresh.enerexStateId ?? fresh.EnerexStateId) === EMPTY_GUID)
        throw new Error("The state identifier is missing. Refresh the page before editing.");
      sopSave.reset();
      setEditingState(fresh);
    } catch (error) {
      snackbar(error.message || "Failed to load state mappings", "error");
    }
  };

  const sopSave = useSopEditSave({
    notify: snackbar,
    onSuccess: async (message) => {
      setEditingState(null);
      snackbar(message, "success");
      detailLoadedRef.current = "";
      historyLoadedRef.current = "";
      await Promise.all([loadDetail(), loadStateOperations(), loadHistory()]);
    },
    onRefresh: async () => {
      const states = await api.getUtilityStateOperations(id);
      setUtilityStates(states);
      setAliasStates(states);
      const stateGuid = editingState?.enerexStateId ?? editingState?.EnerexStateId;
      const fresh = states.find((state) => String(state.enerexStateId ?? state.EnerexStateId).toLowerCase() === String(stateGuid).toLowerCase());
      setEditingState(fresh || null);
    },
  });

  const saveEditedStateOperation = ({ commodityIds, keyNameRequired, keyNameDescription, enrollmentLeadTime, serviceReferenceNumberRequired, serviceReferenceNumberDescription }) => {
    const snapshot = getSopEditSnapshot(editingState);
    const stateGuid = editingState?.enerexStateId ?? editingState?.EnerexStateId;
    if (!snapshot || !stateGuid || stateGuid === EMPTY_GUID || saving) return;
    return sopSave.save({
      EnerexUtilityId: id,
      EnerexStateId: stateGuid,
      CommodityIds: commodityIds,
      ExpectedMappingIds: snapshot.expectedMappingIds,
      ServiceReferenceNumberRequired: serviceReferenceNumberRequired,
      ServiceReferenceNumberDescription: serviceReferenceNumberDescription,
      KeyNameRequired: keyNameRequired,
      KeyNameDescription: keyNameDescription,
      EnrollmentLeadTime: enrollmentLeadTime,
      StateIds: [Number(editingState.stateId ?? editingState.StateId)],
      CommodityMappings: (editingState.commodityMappings ?? editingState.CommodityMappings).map((mapping) => ({
        Id: String(mapping.id ?? mapping.Id),
        CommodityId: Number(mapping.commodityId ?? mapping.CommodityId),
      })),
      UserName: currentUserName,
    });
  };
  const deleteStateOperation = async (row) => {
    setConfirmDelete({
      show: true, row,
      message: "Deleting this State of Operations record will also deactivate its linked Aliases, Rate Classes. Continue?",
      onConfirm: async () => {
        if (saving) return;
        setConfirmDelete(prev => ({ ...prev, show: false }));
        const actionId = getActionId(row);
        if (!actionId) {
          snackbar("Unable to delete state operation: missing record id", "error");
          return;
        }
        setSaving(true);
        try {
          const result = await api.removeUtilityStateOperation(actionId);
          snackbar(responseMessage(result, "Utility state operation and linked records deactivated"), "success");
          await refreshAll();
        } catch (error) {
          snackbar(error.message || "Failed to delete state operation", "error");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveAlias = async (payload) => {
    setSaving(true);
    try {
      const result = await api.createUtilityAlias({
        Id: payload.Id || 0,
        Name: payload.Alias,
        Identifier: id,
        StateId: payload.StateId,
        StateName: payload.StateName,
        CheckValidation: true,
        UserName: currentUserName,
      });
      if (!result || result === "AlreadyExist") {
        snackbar("Alias was not added. It may already exist.", "warning");
        return;
      }
      const nextAliases = await api.getUtilityAliases(id);
      const nextAliasRows = Array.isArray(nextAliases) ? nextAliases : [];
      if (!payload.Id && nextAliasRows.length <= aliases.length) {
        setAliases(nextAliasRows);
        snackbar("Alias was not added. It may already exist.", "warning");
        return;
      }
      setAliases(nextAliasRows);
      snackbar(payload.Id ? "Utility alias updated successfully" : "Utility alias saved successfully", "success");
      setShowAliasModal(false);
      setEditingAlias(null);
      historyLoadedRef.current = "";
      await loadHistory();
    } catch (error) {
      snackbar(error.message || "Failed to save alias", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteAlias = async (row) => {
    setConfirmDelete({
      show: true, row,
      message: "Delete this alias?",
      onConfirm: async () => {
        if (saving) return;
        setConfirmDelete(prev => ({ ...prev, show: false }));
        const actionId = getActionId(row);
        if (!actionId) {
          snackbar("Unable to delete alias: missing record id", "error");
          return;
        }
        setSaving(true);
        try {
          await api.removeUtilityAlias(actionId, currentUserName);
          snackbar("Utility alias deleted", "success");
          historyLoadedRef.current = "";
          await Promise.all([loadAliases(), loadHistory()]);
        } catch (error) {
          snackbar(error.message || "Failed to delete alias", "error");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveRateClass = async (payload) => {
    setSaving(true);
    try {
      const result = await api.saveUtilityRateClass({
        Id: payload.Id || 0,
        CheckValidation: false,
        UtilitiesId: payload.UtilitiesId || 0,
        CommodityId: payload.CommodityId,
        UtilitiesIdentifier: id,
        StateId: payload.StateId,
        RateClass: payload.RateClass,
        IsResidential: Boolean(payload.IsResidential),
        UserName: currentUserName,
      });
      if (result === "AlreadyExist") {
        snackbar("Utility rate class already exists", "warning");
        return;
      }
      if (!result) {
        snackbar("Utility rate class was not added. It may already exist.", "warning");
        return;
      }
      snackbar(payload.Id ? "Utility rate class updated successfully" : "Utility rate class saved successfully", "success");
      setShowRateClassModal(false);
      setEditingRateClass(null);
      rateClassesLoadedRef.current = "";
      await loadRateClasses();
    } catch (error) {
      snackbar(error.message || "Failed to save rate class", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteRateClass = async (row) => {
    setConfirmDelete({
      show: true, row,
      message: "Delete this rate class?",
      onConfirm: async () => {
        if (saving) return;
        setConfirmDelete(prev => ({ ...prev, show: false }));
        const actionId = Number(getActionId(row));
        if (!Number.isInteger(actionId) || actionId <= 0) {
          snackbar("Unable to delete rate class: missing record id", "error");
          return;
        }
        setSaving(true);
        try {
          const result = await api.removeUtilityRateClasses({ rateClassIds: [actionId] });
          snackbar(responseMessage(result, "Utility rate class deleted"), "success");
          rateClassesLoadedRef.current = "";
          await loadRateClasses();
        } catch (error) {
          snackbar(error.message || "Failed to delete rate class", "error");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveFormat = async (payload) => {
    setSaving(true);
    try {
      const selectedState = detailStateOptions.find(
        (s) => String(getStateOptionValue(s)) === String(payload.StateId)
      );
      const result = await api.saveUtilityFormat({
        Id: payload.Id || 0,
        CheckValidation: true,
        UtilitiesId: payload.UtilitiesId || 0,
        CommodityId: payload.CommodityId,
        Pattern: payload.Pattern,
        Regex: payload.Regex,
        Remark: payload.Remark,
        StateIds: payload.StateId ? [payload.StateId] : [],
        StateName: selectedState ? getStateName(selectedState) : "",
        UtilitiesIdentifier: id,
        UserName: currentUserName,
        Active: true,
        Deleted: false,
        Source: "",
      });
      if (result === false || result === null) {
        snackbar("Utility format was not added. It may already exist.", "warning");
        return;
      }
      snackbar(payload.Id ? "Utility format updated successfully" : "Utility format saved successfully", "success");
      setShowFormatModal(false);
      setEditingFormat(null);
      formatsLoadedRef.current = "";
      await loadFormats();
    } catch (error) {
      snackbar(error.message || "Failed to save utility format", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteFormat = async (row) => {
    setConfirmDelete({
      show: true, row,
      message: "Delete this utility format?",
      onConfirm: async () => {
        if (saving) return;
        setConfirmDelete(prev => ({ ...prev, show: false }));
        const actionId = getActionId(row);
        if (!actionId) {
          snackbar("Unable to delete utility format: missing record id", "error");
          return;
        }
        setSaving(true);
        try {
          await api.removeUtilityFormat(actionId, currentUserName);
          snackbar("Utility format deleted", "success");
          formatsLoadedRef.current = "";
          await loadFormats();
        } catch (error) {
          snackbar(error.message || "Failed to delete utility format", "error");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const detailStateOptions = aliasStates.length ? aliasStates : lookupStates;
  const isPOR = Boolean(utility?.purchaseOfReceivables ?? utility?.PurchaseOfReceivables);
  const utilityWebsite = utility?.webSite || utility?.WebSite;

  const detailTableProps = {
    enableSearch: false,
    enablePagination: false,
    enableHideColumns: false,
    enableSorting: false,
    enableFilters: false,
    enableSelection: false,
    enablePaginateAll: false,
    enableColumnVisibilityStorage: false,
    enableGlobalFilterStorage: false,
    enableUrlPagination: false,
  };

  const yesNoIcon = (value) => (
    value ? <i className="uil uil-check text-success" /> : <i className="uil uil-times text-danger" />
  );

  const masterInfoRows = [
    {
      leftLabel: "Name",
      leftValue: textOrDash(utility?.name || utility?.Name),
      rightLabel: "Long Name",
      rightValue: textOrDash(utility?.longName || utility?.LongName),
    },
    {
      leftLabel: "Website",
      leftValue: utilityWebsite ? <a href={utilityWebsite} target="_blank" rel="noreferrer">{utilityWebsite}</a> : textOrDash(),
      rightLabel: "DUNS Number",
      rightValue: textOrDash(utility?.duns || utility?.Duns),
    },
    {
      leftLabel: "Parent Utility",
      leftValue: textOrDash(utility?.parentUtilityName || utility?.ParentUtilityName),
      rightLabel: "Merged With",
      rightValue: textOrDash(utility?.mergedWithUtilityName || utility?.MergedWithUtilityName),
    },
    {
      leftLabel: "Status",
      leftValue: <span className={`badge ${isUtilityActive(utility) ? "bg-primary" : "bg-danger"}`}>{isUtilityActive(utility) ? "Active" : "Inactive"}</span>,
      rightLabel: "POR",
      rightValue: isPOR ? "Yes" : "No",
    },
  ];

  const masterInfoColumns = [
    {
      id: "leftLabel",
      header: "",
      size: 160,
      cell: ({ row }) => row.original.leftLabel,
    },
    {
      id: "leftValue",
      header: "",
      cell: ({ row }) => row.original.leftValue,
    },
    {
      id: "rightLabel",
      header: "",
      size: 160,
      cell: ({ row }) => row.original.rightLabel,
    },
    {
      id: "rightValue",
      header: "",
      cell: ({ row }) => row.original.rightValue,
    },
  ];

  const stateOperationColumns = [
    {
      id: "state",
      header: "State",
      cell: ({ row }) => {
        const stateId = row.original.stateId ?? row.original.StateId;
        const state = stateId == null ? null : lookupStates.find(
          (item) => String(getStateOptionValue(item)) === String(stateId)
        );
        return textOrDash(
          getStateAbbreviation(row.original, lookupStates) ||
          state?.displayName || state?.DisplayName ||
          row.original.displayName || row.original.DisplayName ||
          row.original.stateName || row.original.StateName
        );
      },
    },
    {
      id: "naturalGas",
      header: "Natural Gas",
      cell: ({ row }) => yesNoIcon(Boolean(row.original.gas ?? row.original.Gas ?? row.original.natGas ?? row.original.NatGas)),
    },
    {
      id: "electric",
      header: "Electric",
      cell: ({ row }) => yesNoIcon(Boolean(row.original.electric ?? row.original.Electric)),
    },
    {
      id: "keyNameRequired",
      header: "Name Key Required",
      cell: ({ row }) => yesNoIcon(Boolean(row.original.keyNameRequired || row.original.KeyNameRequired)),
    },
    {
      id: "keyNameDescription",
      header: "Name Key Description",
      cell: ({ row }) => textOrDash(row.original.keyNameDescription || row.original.KeyNameDescription),
    },
    {
      id: "serviceReferenceNumberRequired",
      header: "Service Reference Number Required",
      cell: ({ row }) => yesNoIcon(Boolean(row.original.serviceReferenceNumberRequired ?? row.original.ServiceReferenceNumberRequired)),
    },
    {
      id: "serviceReferenceNumberDescription",
      header: "Service Reference Number Description",
      cell: ({ row }) => textOrDash(row.original.serviceReferenceNumberDescription ?? row.original.ServiceReferenceNumberDescription),
    },
    {
      id: "enrollmentLeadTime",
      header: "Enrollment Lead Time days",
      cell: ({ row }) => textOrDash(row.original.enrollmentLeadTime || row.original.EnrollmentLeadTime),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ActionsMenu align="end">
          <ActionsMenu.ActionItem onClick={() => editStateOperation(row.original)}>Edit</ActionsMenu.ActionItem>
          <ActionsMenu.ActionItem onClick={() => deleteStateOperation(row.original)}>Delete</ActionsMenu.ActionItem>
        </ActionsMenu>
      ),
    },
  ];

  const aliasColumns = [
    {
      id: "alias",
      header: "Alias",
      cell: ({ row }) => textOrDash(row.original.alias || row.original.Alias || row.original.name || row.original.Name),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ActionsMenu align="end">
          <ActionsMenu.ActionItem onClick={() => { setEditingAlias(row.original); setShowAliasModal(true); }}>Edit</ActionsMenu.ActionItem>
          <ActionsMenu.ActionItem onClick={() => deleteAlias(row.original)}>Delete</ActionsMenu.ActionItem>
        </ActionsMenu>
      )
    },
  ];

  const rateClassColumns = [
    {
      id: "state",
      header: "State",
      cell: ({ row }) => textOrDash(getStateAbbreviation(row.original, lookupStates) || getFormatStateName(row.original, lookupStates)),
    },
    {
      id: "commodity",
      header: "Commodity",
      cell: ({ row }) => <CommodityBadges commodityId={row.original.CommodityId || row.original.commodityId} />,
    },
    {
      id: "rateClass",
      header: "Rate Class",
      cell: ({ row }) => textOrDash(row.original.RateClass || row.original.rateClass),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ActionsMenu align="end">
          <ActionsMenu.ActionItem onClick={() => { setEditingRateClass(row.original); setShowRateClassModal(true); }}>Edit</ActionsMenu.ActionItem>
          <ActionsMenu.ActionItem onClick={() => deleteRateClass(row.original)}>Delete</ActionsMenu.ActionItem>
        </ActionsMenu>
      ),
    },
  ];

  const formatColumns = [
    {
      id: "state",
      header: "State",
      cell: ({ row }) => textOrDash(getStateAbbreviation(row.original, lookupStates) || getStateName(row.original, detailStateOptions)),
    },
    {
      id: "commodity",
      header: "Commodity",
      cell: ({ row }) => <CommodityBadges commodityId={row.original.commodityId || row.original.CommodityId} />,
    },
    {
      id: "pattern",
      header: "Pattern",
      cell: ({ row }) => textOrDash(row.original.pattern || row.original.Pattern),
    },
    {
      id: "regex",
      header: "Regex",
      cell: ({ row }) => textOrDash(row.original.regex || row.original.Regex),
    },
    {
      id: "remark",
      header: "Remark",
      cell: ({ row }) => textOrDash(row.original.remark || row.original.Remark),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ActionsMenu align="end">
          <ActionsMenu.ActionItem onClick={() => { setEditingFormat(row.original); setShowFormatModal(true); }}>Edit</ActionsMenu.ActionItem>
          <ActionsMenu.ActionItem onClick={() => deleteFormat(row.original)}>Delete</ActionsMenu.ActionItem>
        </ActionsMenu>
      ),
    },
  ];


  return (
    <>
      <style>{`
        .utility-transfer-dialog { width: calc(100% - 32px); max-width: 480px; margin-left: auto; margin-right: auto; }
      `}</style>
      <div className="">
        <div style={{ alignItems: "center", display: "flex" }}>
          <div style={{ alignItems: "center", display: "flex", flexShrink: 0, height: 64, justifyContent: "center", width: 64 }}>
            <LogoPreview utility={utility} className="utility-detail-logo" imageStyle={{ height: 42, width: "auto" }} />
          </div>
          <h4 style={{ alignItems: "center", display: "flex", fontSize: 30, margin: 0, minHeight: 80 }}>
            {utility?.name || utility?.Name || "Utility"}
          </h4>
        </div>
        <ol className="breadcrumb mb-0 py-1">
  <li className="breadcrumb-item">
    <Link
      to={utilityListLocation}
      className="link-color d-inline-flex align-items-center"
    >
      <i className="uil uil-angle-left utility-back-link-icon" />
      <span>Utilities</span>
    </Link>
  </li>
</ol>
        
      </div>

              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <h4 style={detailSectionTitleStyle}>Master Info</h4>
                <div style={{ display: "flex", gap: 8 }}>
                  <LoadingButton size="sm" variant="primary" className="utility-detail-action-button" onClick={() => { setShowHistory(true); loadHistory(); }}>
                    <i className="uil uil-clock utility-detail-action-icon" />
                    <small>View History</small>
                  </LoadingButton>
                  <LoadingButton size="sm" variant="primary" className="utility-detail-action-button" onClick={() => setShowEdit(true)}>
                    <i className="uil uil-edit utility-detail-action-icon" />
                    <small>Edit</small>
                  </LoadingButton>
                </div>
              </div>
              <div style={detailCardStyle}>
                <div style={detailCardBodyStyle}>
                  <Table
                    id="utility-master-info"
                    columns={masterInfoColumns}
                    data={masterInfoRows}
                    total={masterInfoRows.length}
                    noDataMessage="No master info found."
                    {...detailTableProps}
                  />
                </div>
              </div>

              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 8, marginTop: 16 }}>
                <h4 style={detailSectionTitleStyle}>State Of Operations</h4>
                <LoadingButton size="sm" variant="primary" className="utility-detail-action-button"
                  onClick={() => setShowStateModal(true)} disabled={utilityStates.length > 0 || saving}>
                  <i className="uil uil-plus utility-detail-action-icon" />
                  <small>Add State</small>
                </LoadingButton>
              </div>
              <div style={detailCardStyle}>
                <div style={detailCardBodyStyle}>
                  <Table
                    id="utility-state-operations"
                    columns={stateOperationColumns}
                    data={utilityStates}
                    total={utilityStates.length}
                    noDataMessage="No state operations found."
                    {...detailTableProps}
                  />
                </div>
              </div>

              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 8, marginTop: 16 }}>
                <h4 style={detailSectionTitleStyle}>Utility Formats</h4>
                <LoadingButton size="sm" variant="primary" className="utility-detail-action-button" onClick={() => { setEditingFormat(null); setShowFormatModal(true); }}>
                  <i className="uil uil-plus utility-detail-action-icon" />
                  <small>Add Utility Format</small>
                </LoadingButton>
              </div>
              <div style={detailCardStyle}>
                <div style={detailCardBodyStyle}>
                  <Table
                    id="utility-formats"
                    columns={formatColumns}
                    data={utilityFormats}
                    total={utilityFormats.length}
                    noDataMessage="No utility formats found."
                    {...detailTableProps}
                  />
                </div>
              </div>

              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 8, marginTop: 16 }}>
                <h4 style={detailSectionTitleStyle}>Aliases</h4>
                <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
                  <LoadingButton size="sm" variant="secondary" className="utility-detail-action-button" disabled={!selectedAliasIds.length} onClick={() => { setTransferForm({ utility: "", state: "", commodity: "" }); setBulkAction({ type: "transfer", recordType: "aliases" }); }}><small>Transfer</small></LoadingButton>
                  <LoadingButton size="sm" variant="danger" className="utility-detail-action-button" disabled={!selectedAliasIds.length} onClick={() => setBulkAction({ type: "delete", recordType: "aliases" })}><small>Delete All</small></LoadingButton>
                  <LoadingButton size="sm" variant="primary" className="utility-detail-action-button" onClick={() => { setEditingAlias(null); setShowAliasModal(true); }}>
                    <i className="uil uil-plus utility-detail-action-icon" />
                    <small>Add Alias</small>
                  </LoadingButton>
                </div>
              </div>
              <div style={detailCardStyle}>
                <div style={detailCardBodyStyle}>
                  <Table
                    id="utility-aliases"
                    columns={aliasColumns}
                    data={aliases}
                    total={aliases.length}
                    noDataMessage="No aliases found."
                    {...detailTableProps}
                    enableSelection
                    rowSelection={aliasRowSelection}
                    onRowSelectionChange={setAliasRowSelection}
                    enablePagination
                    pageSizes={[10, 25, 50, 100]}
                    hook={useDetailPagination}
                  />
                </div>
              </div>

              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 8, marginTop: 16 }}>
                <h4 style={detailSectionTitleStyle}>Rate Classes</h4>
                <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
                  <LoadingButton size="sm" variant="secondary" className="utility-detail-action-button" disabled={!selectedRateClassIds.length} onClick={() => { setTransferForm({ utility: "", state: "", commodity: "" }); setBulkAction({ type: "transfer", recordType: "rateClasses" }); }}><small>Transfer</small></LoadingButton>
                  <LoadingButton size="sm" variant="danger" className="utility-detail-action-button" disabled={!selectedRateClassIds.length} onClick={() => setBulkAction({ type: "delete", recordType: "rateClasses" })}><small>Delete All</small></LoadingButton>
                  <LoadingButton size="sm" variant="primary" className="utility-detail-action-button" onClick={() => { setEditingRateClass(null); setShowRateClassModal(true); }}>
                    <i className="uil uil-plus utility-detail-action-icon" />
                    <small>Add Rate Class</small>
                  </LoadingButton>
                </div>
              </div>
              <div style={detailCardStyle}>
                <div style={detailCardBodyStyle}>
                  <Table
                    id="utility-rate-classes"
                    columns={rateClassColumns}
                    data={utilityRateClasses}
                    total={utilityRateClasses.length}
                    noDataMessage="No rate classes found."
                    {...detailTableProps}
                    enableSelection
                    rowSelection={rateClassRowSelection}
                    onRowSelectionChange={setRateClassRowSelection}
                    enablePagination
                    pageSizes={[10, 25, 50, 100]}
                    hook={useDetailPagination}
                  />
                </div>
              </div>

      <UtilityFormModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        onSave={saveMaster}
        utility={utility}
        utilityOptions={utilityOptions}
        saving={saving}
      />
      <ConfirmationModal
        show={bulkAction.type === "delete"}
        dialogClassName="modal-sm"
        centered
        title={`Delete ${bulkRecordLabel}`}
        confirmButtonLabel="Confirm Delete"
        confirmButtonVariant="danger"
        isLoading={bulkSaving}
        onHide={() => { if (!bulkSaving) closeBulkModal(); }}
        onConfirm={confirmBulkDelete}
      >
        <p>Are you sure you want to delete the selected {bulkRecordLabelPlural}?</p>
      </ConfirmationModal>
      <ConfirmationModal
        show={bulkAction.type === "transfer"}
        dialogClassName="utility-transfer-dialog"
        centered
        title={`Transfer ${bulkRecordLabel}`}
        confirmButtonLabel="Transfer"
        confirmButtonVariant="primary"
        isLoading={bulkSaving}
        onHide={() => { if (!bulkSaving) closeBulkModal(); }}
        onConfirm={confirmBulkTransfer}
      >
        <div style={{ display: "grid", gap: 18 }}>
        <div>
           <label htmlFor="transfer-utility" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Utility</label>
          <FormSelect
          styles={{ menu: (base) => ({ ...base, width: "100%", minWidth: "100%" }) }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          inputId="transfer-utility"
          options={utilityOptions.map((option) => ({ value: getEnerexIdentifier(option), label: option.name || option.Name || getEnerexIdentifier(option) })).filter((option) => option.value)}
          value={utilityOptions.map((option) => ({ value: getEnerexIdentifier(option), label: option.name || option.Name || getEnerexIdentifier(option) })).find((option) => option.value === transferForm.utility) || null}
          onChange={(option) => setTransferForm((form) => ({ ...form, utility: option?.value || "" }))}
          placeholder="Select utility"
          isClearable
          />
        </div>
        {bulkAction.recordType === "rateClasses" && <div>
           <label htmlFor="transfer-state" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>State</label>
          <FormSelect
          styles={{ menu: (base) => ({ ...base, width: "100%", minWidth: "100%" }) }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          inputId="transfer-state"
          options={lookupStates.map((state) => ({ value: String(getStateOptionValue(state)), label: getStateName(state, lookupStates) }))}
          value={lookupStates.map((state) => ({ value: String(getStateOptionValue(state)), label: getStateName(state, lookupStates) })).find((option) => option.value === transferForm.state) || null}
          onChange={(option) => setTransferForm((form) => ({ ...form, state: option?.value || "" }))}
          placeholder="Select state"
          isClearable
          />
        </div>}
        {bulkAction.recordType === "rateClasses" && <div>
           <label htmlFor="transfer-commodity" style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Commodity</label>
          <FormSelect
          styles={{ menu: (base) => ({ ...base, width: "100%", minWidth: "100%" }) }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          inputId="transfer-commodity"
          options={[{ value: "1", label: "Electric" }, { value: "2", label: "Natural Gas" }]}
          value={[{ value: "1", label: "Electric" }, { value: "2", label: "Natural Gas" }].find((option) => option.value === transferForm.commodity) || null}
          onChange={(option) => setTransferForm((form) => ({ ...form, commodity: option?.value || "" }))}
          placeholder="Select commodity"
          isClearable
          />
        </div>}
        </div>
      </ConfirmationModal>
      <EditStateOperationModal
        row={editingState}
        stateLabel={getStateName(editingState || {}, lookupStates)}
        saving={sopSave.saving}
        canRefresh={sopSave.canRefresh}
        onRefresh={sopSave.refresh}
        onHide={() => { sopSave.reset(); setEditingState(null); }}
        onSave={saveEditedStateOperation}
      />
      <StateOperationModal
        show={showStateModal}
        onHide={() => setShowStateModal(false)}
        onSave={saveStateOperation}
        countries={countries}
        states={lookupStates}
        existingStates={utilityStates}
        onCountryChange={loadStatesForCountry}
        saving={saving}
      />
      <AliasModal
        show={showAliasModal}
        record={editingAlias}
        onHide={() => { setShowAliasModal(false); setEditingAlias(null); }}
        onSave={saveAlias}
        states={utilityStates}
        stateLookup={lookupStates}
        saving={saving}
      />
      <RateClassModal
        show={showRateClassModal}
        record={editingRateClass}
        onHide={() => { setShowRateClassModal(false); setEditingRateClass(null); }}
        onSave={saveRateClass}
        states={utilityStates}
        saving={saving}
      />
      <FormatModal
        show={showFormatModal}
        record={editingFormat}
        onHide={() => { setShowFormatModal(false); setEditingFormat(null); }}
        onSave={saveFormat}
        states={utilityStates}
        saving={saving}
      />
      <HistoryModal show={showHistory} onHide={() => setShowHistory(false)} history={history} />
      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={confirmDelete.show}
        onHide={() => { if (!saving) setConfirmDelete({ ...confirmDelete, show: false }); }}
        title="Confirm Delete"
        confirmButtonLabel="Delete"
        confirmButtonVariant="danger"
        isLoading={saving}
        keyboard={!saving}
        onConfirm={confirmDelete.onConfirm}
      >
        <p>{confirmDelete.message}</p>
      </ConfirmationModal>
    </>
  );
};
export default UtilityDetailPage;
